import { useEffect, useState } from 'react';
import { Chip, DialogContent, Grid, Paper, Stack, Typography } from '@mui/material';
import { ConfirmationNumberOutlined } from '@mui/icons-material';
import {
  faCalendarAlt,
  faFilePdf,
  faHashtag,
  faImage,
  faMoneyBill,
  faStore,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  MolePrimaryButton,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { Util } from '../../util/Util';
import { Z_INDEX } from '../../styles/zIndex';
import type { VoucherRow } from './voucherTypes';
import { downloadVoucherImage, openPdfFromBase64 } from './voucherExport';
import { canExportPdf } from '../../util/access';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  bundle: VoucherRow;
};

const statusColor = (status: string): 'success' | 'default' | 'error' | 'warning' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'REVOKED') return 'error';
  if (status === 'EXPIRED') return 'warning';
  return 'default';
};

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value?: string | null }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <FontAwesomeIcon icon={icon} style={{ width: 14, color: '#64748b' }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
    <Typography variant="body2" fontWeight={600}>{value || '—'}</Typography>
  </Stack>
);

const VoucherReport = ({ isOpen, toggle, bundle }: Props) => {
  const { api, access } = useAuth();
  const { setLoadingMessage, queueReRun, t } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const [record, setRecord] = useState<VoucherRow | null>(null);
  const currency = appSettings?.BIZ_CURRENCY?.value || 'USD';

  const typeLabel = (value?: string) => {
    const key = String(value || '');
    const translated = t(`common:voucher.types.${key}`);
    return translated.startsWith('common:') ? key : translated;
  };

  const fetchData = async () => {
    if (!bundle?.id) return;
    const res = await api.get(`/vouchers/${bundle.id}`);
    if (res?.status === 200) {
      setRecord(res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else if (res?.status === 404) {
      toast.warn(t('common:toast.warn.noRecord'));
      toggle();
    }
  };

  useEffect(() => {
    if (isOpen && bundle?.id) {
      void fetchData();
    }
    return () => {
      if (!isOpen) setRecord(null);
    };
  }, [isOpen, bundle?.id]);

  const handleExport = async () => {
    if (!bundle?.id || !canExportPdf(access, 'voucher')) return;
    setLoadingMessage(t('common:report.loadingReport'));
    const res = await api.get(`/reports/vouchers/${bundle.id}`);
    setLoadingMessage(null);
    if (res?.status === 200 && res.data?.data) {
      openPdfFromBase64(res.data.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleExport);
    } else {
      toast.error(t('common:report.failedToLoadReport'));
    }
  };

  const row = record || bundle;
  const status = row.status || 'ACTIVE';

  return (
    <MoleDialog
      open={isOpen}
      onClose={toggle}
      maxWidth="md"
      fullWidth
      themeColor={themeColor}
      paperOptions={{ maxHeight: '95vh' }}
      sx={{ zIndex: Z_INDEX.nestedDialog }}
    >
      <MoleDialogHeader
        title={t('common:voucher.reportTitle')}
        subtitle={row.code}
        icon={<ConfirmationNumberOutlined />}
        onClose={toggle}
        themeColor={themeColor}
      />
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderStyle: 'dashed' }}>
              <Stack spacing={1.5}>
                <DetailItem icon={faHashtag} label={t('common:voucher.code')} value={row.code} />
                <DetailItem icon={faMoneyBill} label={t('common:tables.amount')} value={Util.formatCurrency(Number(row.amount || 0), currency)} />
                <DetailItem icon={faHashtag} label={t('common:voucher.type')} value={typeLabel(row.type)} />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 118 }}>{t('common:voucher.status')}:</Typography>
                  <Chip size="small" color={statusColor(status)} label={t(`common:voucher.statuses.${status.toLowerCase()}`)} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              {t('common:tables.details')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
              <Stack spacing={1.5}>
                <DetailItem icon={faUser} label={t('common:voucher.issuer')} value={row.issuer} />
                <DetailItem icon={faUser} label={t('common:voucher.issuee')} value={row.issuee} />
                <DetailItem icon={faCalendarAlt} label={t('common:voucher.issued')} value={row.issued ? Util.formatDate(row.issued, 'full') : '—'} />
                <DetailItem icon={faCalendarAlt} label={t('common:voucher.expiry')} value={row.expiry ? Util.formatDate(row.expiry, 'full') : '—'} />
                <DetailItem icon={faStore} label={t('common:voucher.pos')} value={row.pos_name} />
                <DetailItem icon={faMoneyBill} label={t('common:tables.mode')} value={row.mode_name} />
                <DetailItem icon={faUser} label={t('common:tables.user')} value={row.user} />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        {row.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {row.description}
          </Typography>
        ) : null}
      </DialogContent>
      <MoleDialogActions>
        <MoleCancelButton onClick={toggle}>{t('common:ui.buttons.close')}</MoleCancelButton>
        <MolePrimaryButton
          color="inherit"
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faImage} />}
          onClick={() => {
            void downloadVoucherImage(row, {
              type: typeLabel(row.type),
              issued: row.issued ? Util.formatDate(row.issued, 'medium') : '—',
              expiry: row.expiry ? Util.formatDate(row.expiry, 'medium') : '—',
              status: t(`common:voucher.statuses.${String(row.status || 'ACTIVE').toLowerCase()}`),
              amount: Util.formatCurrency(Number(row.amount || 0), currency),
              issuedLabel: t('common:voucher.issued'),
              expiryLabel: t('common:voucher.expiry'),
              issueeLabel: t('common:voucher.issuee'),
              issuerLabel: t('common:voucher.issuer'),
              generatedLabel: t('common:voucher.generatedAt'),
              voucherLabel: t('common:voucher.reportTitle'),
              presentHint: t('common:voucher.presentHint'),
            }, {
              name: String(appSettings?.BIZ_NAME_FULL?.value || appSettings?.BIZ_BRANCH?.value || ''),
              branch: String(appSettings?.BIZ_BRANCH?.value || ''),
              department: String(appSettings?.BIZ_DEPARTMENT?.value || ''),
              logo: String(appSettings?.BIZ_LOGO?.value || ''),
              accent: themeColor,
            });
          }}
        >
          {t('common:voucher.exportImage')}
        </MolePrimaryButton>
        {canExportPdf(access, 'voucher') && (
          <MolePrimaryButton startIcon={<FontAwesomeIcon icon={faFilePdf} />} onClick={handleExport}>
            {t('common:ui.buttons.exportAsPdf')}
          </MolePrimaryButton>
        )}
      </MoleDialogActions>
    </MoleDialog>
  );
};

export default VoucherReport;
